import type { App } from "vue";
import * as Sentry from "@sentry/vue";
import axios from "axios";
import { HTTP } from "@/api";
import { queryClient } from "@/lib/queryClient";
import { router } from "@/router";
import { getWebsocketSubscriptionCount } from "@/composables/models/websocket/useWebsocket";
import { EEnvironmentType } from "@/stores/environmentStore";
import {
  reportUncleanExits,
  startBrowserDiagnostics,
  type DiagnosticSample,
  type UncleanExitReport,
  type WarningReport,
} from "@/utils/browserDiagnostics";

/**
 * Local builds (`VITE_ENVIRONMENT` unset, which the app treats as DEV) and the DEVEL and QA
 * deployments only. Every other environment never installs anything.
 */
const DIAGNOSTICS_ENVIRONMENTS: string[] = [
  EEnvironmentType.DEV,
  EEnvironmentType.DEVEL,
  EEnvironmentType.QA,
];

export const browserDiagnosticsEnabled =
  import.meta.env.MODE !== "test" &&
  DIAGNOSTICS_ENVIRONMENTS.includes(import.meta.env.VITE_ENVIRONMENT || EEnvironmentType.DEV);

/**
 * Starting points only. Tune them against the first week of `browser.health` logs from
 * healthy sessions. Growth values are measured above each route's own lowest value.
 */
const LIMITS = {
  heapUsedMiB: 1500,
  inFlightRequests: 50,
  pollingQueries: 30,
  websocketSubscriptions: 50,
};
const GROWTH = {
  heapUsedMiB: 300,
  mountedComponents: 1500,
  domNodes: 15_000,
  cachedQueries: 500,
  queryObservers: 300,
};

let inFlightRequests = 0;
let mountedComponents = 0;

/** Every page load by default; `VITE_BROWSER_DIAGNOSTICS_SAMPLE_RATE` (0–1) can lower it. */
function resolveSampleRate(): number {
  const rate = Number.parseFloat(import.meta.env.VITE_BROWSER_DIAGNOSTICS_SAMPLE_RATE);
  return Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : 1;
}

/** Wraps the adapter rather than using interceptors, so every real request (including token-refresh retries, failures and cancellations) is counted exactly once. */
function countHttpRequests() {
  const adapter = axios.getAdapter(HTTP.defaults.adapter);
  HTTP.defaults.adapter = async (config) => {
    inFlightRequests++;
    try {
      return await adapter(config);
    } finally {
      inFlightRequests--;
    }
  };
}

function queryCounters() {
  const queries = queryClient.getQueryCache().getAll();
  let queryObservers = 0;
  let pollingQueries = 0;
  for (const query of queries) {
    queryObservers += query.observers.length;
    if (query.observers.some((observer) => observer.options.refetchInterval)) pollingQueries++;
  }
  return { cachedQueries: queries.length, queryObservers, pollingQueries };
}

/** Sentry log attributes and contexts must be flat primitives. */
function flatten(sample: DiagnosticSample): Record<string, string | number | boolean> {
  const { counters, ...rest } = sample;
  const attributes: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined) attributes[key] = value;
  }
  for (const [key, value] of Object.entries(counters)) attributes[`counter.${key}`] = value;
  return attributes;
}

function onSentryWarning({ reasons, growth, current, history }: WarningReport) {
  Sentry.captureEvent(
    {
      message: "Browser resource growth suspected",
      level: "warning",
      fingerprint: ["browser-resource-growth-v1", reasons[0] ?? "unknown"],
      tags: {
        diagnostic: "browser-health",
        route_name: current.routeName,
        diagnostic_session: current.session,
      },
      contexts: { browser_health: flatten(current) },
      extra: {
        reasons: reasons.join(", "),
        growth: growth.map((g) => `${g.metric} +${g.excess} (>= ${g.threshold})`).join(", "),
      },
    },
    {
      attachments: [
        {
          filename: "browser-health-history.json",
          data: JSON.stringify(history),
          contentType: "application/json",
        },
      ],
    }
  );
}

function onSentryUncleanExit({ previous, startedAt, lastSeenAt, wasDiscarded }: UncleanExitReport) {
  Sentry.captureEvent({
    message: "Previous browser session ended uncleanly",
    level: "warning",
    fingerprint: ["browser-unclean-exit-v1"],
    tags: {
      diagnostic: "browser-health",
      route_name: previous.routeName,
      diagnostic_session: previous.session,
      was_discarded: String(wasDiscarded),
      last_hidden: String(previous.hidden),
    },
    contexts: { browser_health: flatten(previous) },
    extra: { startedAt, lastSeenAt },
  });
}

const sentrySinks = {
  onHeartbeat: (sample: DiagnosticSample) => Sentry.logger.info("browser.health", flatten(sample)),
  onWarning: onSentryWarning,
  onUncleanExit: onSentryUncleanExit,
};

/** Local runs usually have no Sentry DSN; report to the console there instead. */
const consoleSinks = {
  onHeartbeat: (sample: DiagnosticSample) => console.debug("[browser-health]", flatten(sample)),
  onWarning: ({ reasons, growth, current, history }: WarningReport) =>
    console.warn("[browser-health] resource growth suspected", {
      reasons,
      growth,
      current,
      history,
    }),
  onUncleanExit: (report: UncleanExitReport) =>
    console.warn("[browser-health] previous session ended uncleanly", report),
};

/**
 * Browser health diagnostics for local, DEVEL and QA only (see `browserDiagnosticsEnabled`).
 * Reports to Sentry when it is initialised, otherwise to the console. Call after
 * `Sentry.init` and before `app.mount`.
 */
export function installBrowserDiagnostics(app: App): void {
  if (!browserDiagnosticsEnabled) return;
  const rate = resolveSampleRate();
  if (rate <= 0) return;
  const sinks = Sentry.getClient() ? sentrySinks : consoleSinks;

  void reportUncleanExits(sinks.onUncleanExit);
  // Sampling decision only; nothing security-relevant depends on it.
  // eslint-disable-next-line sonarjs/pseudo-random
  if (Math.random() >= rate) return;

  countHttpRequests();
  app.mixin({
    mounted() {
      mountedComponents++;
    },
    unmounted() {
      mountedComponents--;
    },
  });

  startBrowserDiagnostics({
    getRouteName: () => String(router.currentRoute.value.name ?? "unnamed"),
    getCounters: () => ({
      inFlightRequests,
      mountedComponents,
      websocketSubscriptions: getWebsocketSubscriptionCount(),
      ...queryCounters(),
    }),
    onHeartbeat: sinks.onHeartbeat,
    onWarning: sinks.onWarning,
    limits: LIMITS,
    growth: GROWTH,
  });
}
